import { ROOT_HOST } from "@/shared/constants/env/internalService.constants";
import { getCDNURL } from "@/shared/utils/commons";
import Head from "next/head";

const commonMeta = {
  twitterUsername: "@graphyapp",
  ogImage: "/meta-image.png",
  ogImageKorea: "/meta-image-korea.png",
};

export type GraphyHeadPropType = {
  title: string;
  description: string;
  canonicalPath: string;
  countryCode: string;
  /** Optional page-specific OG/Twitter image URL. Falls back to the
   *  site-wide meta image when not provided. */
  ogImage?: string;
  /** Keeps the page out of search results. Use for unlisted pages that are
   *  reachable only via their direct URL. */
  noindex?: boolean;
};

const GraphyHead = ({
  title,
  description,
  canonicalPath,
  countryCode,
  ogImage,
  noindex,
}: GraphyHeadPropType) => {
  return (
    <Head>
      <title>{title}</title>
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={(() => {
        const host = (ROOT_HOST ?? "www.graphy.com").replace(/^https?:\/\//, "").replace(/\/$/, "");
        const path = canonicalPath.replace(/^\//, "").replace(/\/$/, "");
        return `https://${host}/${path ? `${path}/` : ""}`;
      })()} />
      <meta name="description" content={description} key="description" />
      <meta
        property="og:description"
        content={description}
        key="ogDescription"
      />
      <meta property="og:title" content={title} key="ogTitle" />

      <meta
        property="og:image"
        content={
          ogImage
            ? ogImage
            : countryCode === "KR"
            ? getCDNURL() + "graphy-web/meta-image-korea.png"
            : getCDNURL() + "graphy-web/meta-image.png"
        }
      />
      <meta
        property="og:image:secure_url"
        content={
          ogImage
            ? ogImage
            : countryCode === "KR"
            ? getCDNURL() + "graphy-web/meta-image-korea.png"
            : getCDNURL() + "graphy-web/meta-image.png"
        }
      />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1006" />
      <meta property="og:image:height" content="526" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={commonMeta.twitterUsername} />
      <meta name="twitter:creator" content={commonMeta.twitterUsername} />
      <meta property="og:url" content="https://graphy.com/" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={
          ogImage
            ? ogImage
            : countryCode === "KR"
            ? getCDNURL() + "graphy-web/meta-image-korea.png"
            : getCDNURL() + "graphy-web/meta-image.png"
        }
      />
      {/* IndexNow ownership verification for Bing / Perplexity / Copilot */}
      <meta name="msvalidate.01" content="9caf349eb39f49e4bf4568800efd0197" />
      {/* Organization / SoftwareApplication / WebSite schema is injected
          site-wide via _document.tsx — no duplicate needed here */}
    </Head>
  );
};

export default GraphyHead;
